import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconClose } from "./icons";
import { useSwipeClose } from "./DetailPanel";

/** Dialoogvenster: Escape sluit, focus gaat naar het eerste veld en terug naar waar je was. */
export function Modal({
  id: dialogId,
  title,
  onClose,
  children,
  footer,
  wide = false,
  busy = false,
}: {
  /** Id van het venster, voor `aria-controls` op de knop die het opent. */
  id?: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  busy?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  // Telefoon: het venster is een sheet van onderen; omlaag vegen sluit (niet tijdens opslaan).
  const swipeRef = useSwipeClose<HTMLDivElement>(() => !busy && close.current());

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input:not([disabled]), select, textarea, [data-autofocus]");
    (first ?? ref.current)?.focus();
    return () => prev?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Alleen het bovenste venster (een snel-toevoegen-venster boven een formulier) reageert.
      const open = document.querySelectorAll(".modal");
      if (open[open.length - 1] !== ref.current) return;
      // Escape in een opengeklapte zoekkiezer sluit eerst alleen de lijst.
      const act = document.activeElement as HTMLElement | null;
      const expanded = act?.getAttribute("role") === "combobox" && act.getAttribute("aria-expanded") === "true";
      if (e.key === "Escape" && !busy && !expanded) {
        e.stopPropagation();
        close.current();
      }
      if (e.key === "Tab" && ref.current) {
        // Focus binnen het venster houden.
        const els = [
          ...ref.current.querySelectorAll<HTMLElement>(
            "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex='0']",
          ),
        ];
        if (!els.length) return;
        const first = els[0]!;
        const last = els[els.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [busy]);

  // Via een portal in <body>: een venster uit een paneel met eigen stapelvolgorde (bijv. het assetpaneel) valt
  // anders onder de bovenbalk en de kop van het venster raakt uit beeld.
  return createPortal(
    <div className="modalbg" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div
        className={`modal ${wide ? "wide" : ""}`}
        id={dialogId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        ref={(n) => {
          ref.current = n;
          swipeRef(n);
        }}
        tabIndex={-1}
      >
        <div className="mhead">
          <h2 id={id}>{title}</h2>
          <button type="button" className="btn ghost small" onClick={onClose} aria-label="Sluiten" disabled={busy}>
            <IconClose size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="mbody">{children}</div>
        {footer && <div className="mfoot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** "Verwijderen"-knop met bevestiging in het venster zelf (zoals in het prototype). */
export function ConfirmDelete({
  label,
  question,
  detail,
  onConfirm,
  busy,
  confirmLabel = "Definitief verwijderen",
}: {
  label: string;
  confirmLabel?: string;
  question: string;
  detail: ReactNode;
  onConfirm: () => void;
  busy?: boolean;
}) {
  return (
    <details className="confirm">
      <summary className="btn small danger">{label}</summary>
      <div className="note danger-note" role="alert">
        <strong>{question}</strong>
        <br />
        {detail}
      </div>
      <button type="button" className="btn danger" onClick={onConfirm} disabled={busy}>
        {confirmLabel}
      </button>
    </details>
  );
}
